import { AsyncPipe, DecimalPipe } from "@angular/common";
import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { RouterLink } from "@angular/router";
import { IconComponent } from "@components/icon/icon.component";
import { PillboxComponent } from "@components/pillbox/pillbox.component";
import { NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import { Subject, combineLatest } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IOperation } from "~app/features/planera/model/IOperation";
import { ArbetsorderService } from "~app/features/planera/services/arbetsorder.service";
import { Atgard, VKAtgardArbetsorderOperationDTO } from "~app/features/virkeskop/models/atgard-models";
import { environment } from "~environments/environment";
import { ProduktionsstatusPipe } from "../../../../common/pipes/produktionsstatus.pipe";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { LeveransvirkesvolymMediatorService } from "../../services/leveransvirkesvolym-mediator.service";
import { VirkeskostnaderMediatorService } from "../../services/virkeskostnader-mediator.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";
import { VirkeskopValidationIconPopoverComponent } from "../../validation/validation-icon-popover/virkeskop-validation-icon-popover.component";
import { AtgardBadgeComponent } from "../atgard-badge/atgard-badge.component";
import { AtgardHeaderIconsComponent } from "../atgard-header-icons/atgard-header-icons.component";

@Component({
    selector: "rask-atgard-header",
    templateUrl: "./atgard-header.component.html",
    styleUrls: ["./atgard-header.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        AtgardBadgeComponent,
        VirkeskopValidationIconPopoverComponent,
        AtgardHeaderIconsComponent,
        PillboxComponent,
        NgbPopover,
        IconComponent,
        RouterLink,
        AsyncPipe,
        DecimalPipe,
        ProduktionsstatusPipe
    ]
})
export class AtgardHeaderComponent implements OnInit, OnDestroy {
    @Input() index: number;

    protected volym: number;

    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly leveransvirkesvolymMediatorService = inject(LeveransvirkesvolymMediatorService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly virkeskostnadMediatorService = inject(VirkeskostnaderMediatorService);
    private readonly vkKartaMediatorService = inject(VkKartaMediatorService);

    get atgard(): Atgard {
        return  this.atgardMediatorService.atgard;
    }

    get kontrakt$() {
        return this.kontraktMediatorService.kontrakt$;
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        if(this.atgard.isLeveransvirke) {
            combineLatest([
                this.leveransvirkesvolymMediatorService.leveransvirkesvolymer$,
                this.leveransvirkesvolymMediatorService.virkeskostnader$
            ])
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(([
                    volymer,
                    virkeskostnader
                ]) => {
                    const atgardVolymer = volymer.filter(x => x.virkeskopsatgardLeveransvirkeGuid === this.atgard.guid) || [];
                    const getKonverteringsfaktor = (virkeskostnadGuid: string) =>
                        virkeskostnader.find(v => v.guid === virkeskostnadGuid)?.konverteringsfaktorHandelssortimentMattslag ?? 0;

                    this.volym = ObjectUtils.hasValue(atgardVolymer) ?
                        atgardVolymer.map(v => v.kvantitet * getKonverteringsfaktor(v.virkeskostnadGuid)).reduce((a, b) => a + b, 0) : 0;
                });
        } else {
            this.virkeskostnadMediatorService.virkeskostnader$
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(kostnader => {
                    this.volym = ObjectUtils.hasValue(kostnader) ? this.virkeskostnadMediatorService.volymForAtgard(this.atgardMediatorService.atgard.guid) : 0;
                });
        }
    }

    protected hoverRow(atgardGuid: string, mouseover: boolean) {
        this.vkKartaMediatorService.highlightAtgarder([atgardGuid], mouseover);
    }

    protected isSchemalagd() {
        const operationer: IOperation[] = [];
        // eslint-disable-next-line sonarjs/array-callback-without-return
        this.atgard?.arbetsorder?.operationer.map(o => this.mapOperation(o, operationer));
        return this.arbetsorderService.isSchemalagd(this.atgard?.arbetsorder?.periodsattPeriod, operationer);
    }

    protected prologLink = (): string => `${environment.prologBaseUrl}/Web/avverkning/ao/${this.atgard?.arbetsorder?.beteckning}`;

    private mapOperation(o: VKAtgardArbetsorderOperationDTO, operationer: IOperation[]): void {
        const op = {
            id: o.id,
            namn: o.namn,
            typ: o.typ,
            operationstyp: o.operationstyp,
            skordningPaborjadDatum: o.skordningPaborjadDatum,
            skotningAvslutadDatum: o.skotningAvslutadDatum
        } as unknown as IOperation;

        operationer.push(op);
    }
}
